/**
 * Human-readable labels for the proposal-review UI.
 *
 * The Precision Care UI is used by non-technical clinicians: it must NEVER show
 * a raw backend id (`ENT-…`) or an enum-like token (`entity_created`,
 * `lagging_skill`). These helpers map the ingestion `change_type` enum and card
 * `kind` tokens to plain English. Pure functions, no PHI.
 */

/**
 * Human-readable labels for the backend `change_type` enum
 * (`app.models.ingestion.IngestionChangeType`).
 *
 * Unknown/future values fall back to a title-cased, de-underscored form
 * ({@link humanizeKind}).
 */
const CHANGE_TYPE_LABELS: Record<string, string> = {
  entity_created: "New card",
  entity_updated: "Updated",
  link_added: "Link added",
  contradiction_flagged: "Conflict to review",
  tier1_promotion_proposed: "Promoted finding",
};

/**
 * Turn a snake_case / enum-like token into a human "Sentence case" label.
 *
 * `lagging_skill` → "Lagging skill", `entity_created` → "Entity created". Used
 * for card kinds and as the fallback for unknown `change_type` values.
 *
 * @param value - The raw token.
 * @returns The humanised label (or the value unchanged if it has no body).
 */
export function humanizeKind(value: string): string {
  const words = value.replace(/[-_]+/g, " ").trim();
  if (words === "") return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Human-readable label for a `change_type`, never the raw enum.
 *
 * @param changeType - The backend change-type string.
 * @returns A plain-English label.
 */
export function humanizeChangeType(changeType: string): string {
  return CHANGE_TYPE_LABELS[changeType] ?? humanizeKind(changeType);
}

/**
 * A backend-scheme `source_ref` (e.g. `derivative:<id>`, `chat:<thread>`): a
 * leading `scheme:` made of letters/underscores. These carry an opaque backend
 * id after the scheme and must never be shown raw to a clinician.
 */
const BACKEND_SCHEME_RE = /^[a-z][a-z0-9_]*:/i;

/**
 * The clinician-facing label for a run's source, never a raw backend id.
 *
 * Prefers the backend-resolved human `source_title`. When absent, the raw
 * `source_ref` is shown ONLY when it is not a backend-scheme ref — a plain id
 * such as `SES-001` is acceptable display text, but a `derivative:<uuid>` /
 * `chat:<thread>` ref is a backend id and is suppressed (returns `null`) so the
 * caller can fall back to the friendly summary headline instead. This enforces
 * the human-readable-UI hard gate (never surface a backend id/UUID).
 *
 * @param run - The run's `source_title` (optional) and `source_ref`.
 * @returns The display label, or `null` when no human-readable label exists.
 */
export function sourceDisplayLabel(run: {
  source_title?: string | null;
  source_ref: string;
}): string | null {
  const title = run.source_title;
  if (title !== null && title !== undefined && title.trim() !== "") {
    return title;
  }
  if (!BACKEND_SCHEME_RE.test(run.source_ref)) {
    return run.source_ref;
  }
  return null;
}
